import {
  api_portalOfficeHospitalListing_responseFilter,
  api_portalOfficeListing_filter,
  api_portalOfficeListingAllType_requestSearchPortalOfficeListingsAllTypeUseEngine,
  service_RequestFilter,
} from '@/init/swagger';
import { SearchPublicHomeType } from '@/schemas/SearchPublicHomeSchema';
import {
  APP_HOME_LIMIT,
  HOSPITAL_SEARCH_FILTER,
  LISTING_FILTER_CATEGORY_TYPE,
  STATION_ADVANCED_SEARCH_INITIAL_ZIPCODE,
} from '@/types/types';
import * as v from 'valibot';

export const SearchPublicHomeParamsSchema = v.object({
  /**
   * 郵便番号
   */
  postCode: v.fallback(v.string(), ''),
  /**
   * ST: サービス/介護/医療/対応可能な施術/資格/アピールポイント/その他
   * 医療機関: 対応可能な施術/アピールポイント
   * `${category_id}_${id}`
   */
  searchFilters: v.fallback(
    v.pipe(
      v.union([v.string(), v.array(v.string())]),
      v.transform((value) => (Array.isArray(value) ? value : [value])),
    ),
    [],
  ),
  /**
   * 医療機関: 診療科目
   */
  medicalSubjects: v.fallback(
    v.pipe(
      v.union([v.string(), v.array(v.string())]),
      v.transform((value) =>
        Array.isArray(value) ? value.map(Number) : [Number(value)],
      ),
    ),
    [],
  ),
  /**
   * 医療機関: 在宅医療に関する届出加算
   */
  reportedAdditionalFees: v.fallback(
    v.pipe(
      v.union([v.string(), v.array(v.string())]),
      v.transform((value) =>
        Array.isArray(value) ? value.map(Number) : [Number(value)],
      ),
    ),
    [],
  ),
  /**
   * ページ番号
   */
  page: v.fallback(
    v.pipe(
      v.string(),
      v.transform((v) => Number(v)),
      v.integer(),
    ),
    1,
  ),
});

export type SearchPublicHomeParamsInput = v.InferInput<
  typeof SearchPublicHomeParamsSchema
>;

export type SearchPublicHomeParamsOutput = v.InferOutput<
  typeof SearchPublicHomeParamsSchema
>;

export const buildRequestBodyForPublic = ({
  validatedSearchParams,
  formValues,
  searchFilters,
  hospitalSearchFilters,
}: {
  validatedSearchParams: SearchPublicHomeParamsOutput;
  formValues: SearchPublicHomeType;
  searchFilters: api_portalOfficeListing_filter[];
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
}): {
  body: api_portalOfficeListingAllType_requestSearchPortalOfficeListingsAllTypeUseEngine;
  page: number;
} => {
  const page = formValues.page || validatedSearchParams.page;
  const offset = (page - 1) * APP_HOME_LIMIT;

  const { filters, not_in_filters } = mapSearchFilters({
    validatedSearchParams,
    searchFilters,
    hospitalSearchFilters,
  });

  const body = {
    /**
     * フィルター条件
     */
    filters,
    /**
     * 件数制限
     */
    limit: APP_HOME_LIMIT,
    /**
     * 診療科目
     */
    medical_subjects:
      formValues.medical_subjects || validatedSearchParams.medicalSubjects,
    /**
     * 届出加算
     */
    reported_additional_fees:
      formValues.reported_additional_fees ||
      validatedSearchParams.reportedAdditionalFees,
    /**
     * 除外フィルター条件
     */
    not_in_filters,
    /**
     * 指定された値以降の位置を取得
     */
    offset,
    /**
     * 所在地
     */
    location: {
      /**
       * 郵便番号
       */
      zipcode: formValues.zipcode || validatedSearchParams.postCode,
      /**
       * 自転車分数
       * NOTE: UIでは指定できないが、APIリクエストには必要なので固定値を設定しておく
       */
      bicycle_minutes: 20,
    },
  };
  return { body, page };
};

function mapSearchFilters({
  validatedSearchParams,
  searchFilters,
  hospitalSearchFilters,
}: {
  validatedSearchParams: SearchPublicHomeParamsOutput;
  searchFilters: api_portalOfficeListing_filter[];
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
}): {
  filters: service_RequestFilter[];
  not_in_filters: service_RequestFilter[];
} {
  const searchFilterSet = new Set(searchFilters.map((f) => f.id));
  const hospitalSearchFilterSet = new Set(
    hospitalSearchFilters.map((f) => f.id),
  );

  const filters: service_RequestFilter[] = [];
  const not_in_filters: service_RequestFilter[] = [];

  validatedSearchParams.searchFilters.forEach((filter) => {
    const [categoryIdStr, searchFilterIdStr] = filter.split('_');
    const categoryId = Number(categoryIdStr);
    const searchFilterId = Number(searchFilterIdStr);

    switch (categoryId) {
      // サービス
      case LISTING_FILTER_CATEGORY_TYPE.SERVICE:
        if (searchFilterSet.has(searchFilterId)) {
          filters.push({
            category_id: categoryId,
            search_filter_id: searchFilterId,
          });
        }
        break;
      // 対応可能な施術
      case LISTING_FILTER_CATEGORY_TYPE.MEDICAL_CARE:
      case HOSPITAL_SEARCH_FILTER.MEDICAL_CARE_CATEGORY_ID:
        // NOTE: STと医療機関のいずれかに存在するはず
        if (
          searchFilterSet.has(searchFilterId) ||
          hospitalSearchFilterSet.has(searchFilterId)
        ) {
          filters.push({
            category_id: categoryId,
            search_filter_id: searchFilterId,
          });
        }
        break;
    }
  });

  return { filters, not_in_filters };
}

/**
 * SearchParamsを元に、フォームの初期値を構築
 */
export const buildFormValuesForPublic = ({
  validatedSearchParams,
  searchFilters,
  hospitalSearchFilters,
}: {
  validatedSearchParams: SearchPublicHomeParamsOutput;
  searchFilters: api_portalOfficeListing_filter[];
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
}): SearchPublicHomeType => {
  // 郵便番号が空の場合の郵便番号の初期値を設定
  const initialValuePostCode = !validatedSearchParams.postCode
    ? STATION_ADVANCED_SEARCH_INITIAL_ZIPCODE
    : validatedSearchParams.postCode;

  const selectedFilters = new Set(validatedSearchParams.searchFilters);

  const allFilterKeys = new Set<string>();

  searchFilters.forEach((f) => {
    if (f.category_id != null && f.id != null) {
      allFilterKeys.add(`${f.category_id}_${f.id}`);
    }
  });

  hospitalSearchFilters.forEach((f) => {
    if (f.category_id != null && f.id != null) {
      allFilterKeys.add(`${f.category_id}_${f.id}`);
    }
  });
  const filters: Record<string, boolean> = {};
  allFilterKeys.forEach((key) => {
    filters[key] = selectedFilters.has(key);
  });

  const formValues: SearchPublicHomeType = {
    zipcode: initialValuePostCode,
    page: validatedSearchParams.page,
    medical_subjects: validatedSearchParams.medicalSubjects,
    reported_additional_fees: validatedSearchParams.reportedAdditionalFees,
    filters,
  };

  return formValues;
};
